import { createReducer, on } from "@ngrx/store";
import { StandingAuthCheckActions, StandingAuthInternalTransferActions, StandingAuthWireActions, StandingAuthAchActions, StandingAuthConfirmationPageActions, StandingAuthDetailsActions, StandingAuthReviewPageActions } from "../actions";
import { AchStandingAuthForm, CheckStandingAuthDetails, StandingAuthFormServiceError, StandingAuthorizationForm, isAchFormSA } from "../models/standingAuthorizationForm";
import { routerNavigationAction } from "@ngrx/router-store";
import { Constants, amountTypeDict, checkRecipientTypeDict, navRouteConstants, transactionTypes } from "../../shared/utilities/Constants";
import { FormatPercentPipe } from "../../shared/pipes/FormatPercentPipe";
import { FormatDatePipe } from "../../shared/pipes/FormatDatePipe";

export const key = 'stagedStandingAuthorizationForm';

export type State = StandingAuthorizationForm;

export const initialStandingAuthFormServiceError: StandingAuthFormServiceError = {
    status: 0,
    url: '',
    name: '',
    message: '',
    correlationId: '',
    hasError: false
}

export const initialStandingAuthFormState: State = {
    loading: false,
    error: '',
    hasError: false,
    transactionType: '',
    valid: false,
    standingAuthorizationDetails: {},
    serviceError: initialStandingAuthFormServiceError,
    prefilledFlow: {
        state: '',
        name: ''
      },
};

export const reducer = createReducer(
    initialStandingAuthFormState,
    on(
        StandingAuthWireActions.callWiresVerifyV3,
        StandingAuthWireActions.loadWiresReviewPageWithoutVerifyCall,
        StandingAuthCheckActions.loadChecksReviewPageWithoutVerifyCall,
        StandingAuthInternalTransferActions.callInternalTransferVerify,
        StandingAuthInternalTransferActions.loadInternalTransferReviewPageWithoutVerifyCall,
        StandingAuthCheckActions.callVerify,
        StandingAuthAchActions.callAchVerify,
        (_state, standingAuthForm) => {
            return {
                ...standingAuthForm,
            };
        }
    ),
    on(StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
        StandingAuthConfirmationPageActions.returnStandingAuthClicked,
        StandingAuthReviewPageActions.cancelClicked,
        () => {
          return initialStandingAuthFormState;
        }
    ),
    on(routerNavigationAction, (state, routeParams) => {
        if (
            routeParams.payload.routerState.url === navRouteConstants.createTransaction
        ) {
            return initialStandingAuthFormState;
        }
        return { ...state };
    }),
    on(StandingAuthWireActions.verifyV3CallSuccessful, (state, {response}) => {
        const {
            name,
            postalAddress,
        } = response.toDetails.isoBeneficiary;

        return {
            ...state,
            standingAuthorizationDetails: {
                ...state.standingAuthorizationDetails,
                receivingAccountHolderName: name,
                receivingAccountHolderAddress: postalAddress,
            }
        };
    }),
    on(StandingAuthAchActions.verifyCallSuccessful, (state, {response, initiationDate}) => {
        const formatDatePipe = new FormatDatePipe();
        return {
            ...state,
            standingAuthorizationDetails: {
                ...state.standingAuthorizationDetails,
                withdrawalDate: formatDatePipe.transform(initiationDate),
                completionDate: response.processDate
            }
        }
    }),
    on(StandingAuthCheckActions.inquiryCallSuccessful, (state, response) => {
      const percentFormatPipe = new FormatPercentPipe();
      let { checkInquiryResponse } = response;
      let { cashieringPayeeDetails } = checkInquiryResponse.cashieringCheckInputDetails;
      let { cashieringAddressDetails } = checkInquiryResponse.cashieringCheckInputDetails;
      let { cashieringCheckTransaction } = checkInquiryResponse.cashieringCheckInputDetails;
      const isRecipientAccountHolder = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressofrecord';
      const isThirdPartyInternational = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressforeign';
      return {
        ...state,
        loading: false,
        serviceError: initialStandingAuthFormServiceError,
        transactionType: transactionTypes.check,
        extraPrompts: {
          ...state.extraPrompts,
        },
        standingAuthorizationDetails: {
          ...state.standingAuthorizationDetails,
          taxWithholdingsFormGroup: {
            ...state.standingAuthorizationDetails.taxWithholdingsFormGroup,
            distributionReason: checkInquiryResponse.distributionReasonDescription,
            fedTaxPercent: cashieringCheckTransaction.iraTxn ? percentFormatPipe.transform(checkInquiryResponse.fedTaxRate.toString()) : '',
            stateTaxPercent: cashieringCheckTransaction.iraTxn ? percentFormatPipe.transform(checkInquiryResponse.stateTaxRate.toString()) : '',
          },
          payeeName: cashieringPayeeDetails.payeeDetails.payeeName1,
          payeeType: checkInquiryResponse.isThirdParty ? 'thirdParty' : 'firstParty',
          recipient: checkRecipientTypeDict[cashieringAddressDetails.recipientAddressType as keyof typeof checkRecipientTypeDict],
          checkReason: checkInquiryResponse?.withdrawalReasonCode,
          checkMemo: checkInquiryResponse.memo,
          authorizationNickname: checkInquiryResponse.nickName,
          useConstructedPayee: cashieringPayeeDetails.useConstructedPayee,
          payee: [
            checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName1,
            checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName2 ?? '',
            checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName3 ?? ''
          ],
          distributionReason: checkInquiryResponse.distributionReasonDescription,
          addresseeName: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[0]?.mailName ?? '',
          addresseeNameCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[1]?.mailName ?? '',
          addresseeNameCont2: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[2]?.mailName ?? '',
          streetAddress: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine1 ?? '',
          streetAddressCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine2 ?? '',
          city: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.city ?? '',
          zipCode: (isRecipientAccountHolder || isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
            ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
            : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
          state: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateCode,
          country: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.countryCode,
          stateProvince: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateName,
          postalCode: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
            ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
            : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
        } as CheckStandingAuthDetails,
      }
    }),
    on(StandingAuthAchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
        const percentFormatPipe = new FormatPercentPipe();
        const dateFormatPipe = new FormatDatePipe();
        return {
          ...state,
          transactionType: transactionTypes.ach,
          extraPrompts: {
            ...state.extraPrompts,
            transferDirection: achInquiryResponse.incoming ? 'incoming' : 'outgoing'
          },
          standingAuthorizationDetails: {
            ...state.standingAuthorizationDetails,
            /*
              TODO: Withdrawal date received from Inquiry call is sometimes incorrect (doesn't take into account federal holidays)
            */
            recurringFrequency: achInquiryResponse.frequency,
            endDate: achInquiryResponse.endDate ? dateFormatPipe.transform(achInquiryResponse.endDate) : '',
            secondProcessDate: achInquiryResponse.secondProcessDate && achInquiryResponse.secondProcessDate !== Constants.defaultMaxDate ? 
              dateFormatPipe.transform(achInquiryResponse.secondProcessDate ?? '') : '',
            withdrawalDate: achInquiryResponse.nextProcessDate ? dateFormatPipe.transform(achInquiryResponse.nextProcessDate ?? '') : '',
            completionDate: dateFormatPipe.transform(achInquiryResponse.processDate ?? ''),
            amount: achInquiryResponse.grossNetIndicator === 'G' ? achInquiryResponse.grossAmt : achInquiryResponse.netAmt,
            amountType: achInquiryResponse.grossNetIndicator === 'N' ? amountTypeDict.net : amountTypeDict.gross,
            contributionReason: achInquiryResponse.contributionReason,
            trustIncomePercent: achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(achInquiryResponse.trustFields.trustIncmPct) : '',
            trustPrincipalPercent: achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(achInquiryResponse.trustFields.trustPrinPct) : '',
            trustTaxCode: achInquiryResponse.trustFields.isTrust ? achInquiryResponse.trustFields.trustTaxCode : '',
            trustPaidFor: achInquiryResponse.trustFields.paidFor,
            trustComment: achInquiryResponse.trustFields.tranMemo,
            taxWithholdingsFormGroup: {
              ...state.standingAuthorizationDetails.taxWithholdingsFormGroup,
              distributionReason: achInquiryResponse.iraFields.isIraTransaction ? achInquiryResponse.distributionReasonDescription : '',
              fedTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.fedTaxPercentage) : '',
              stateTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.stateTaxPercentage) : '',
            }
          }
        } as AchStandingAuthForm;
    }),
    on(StandingAuthDetailsActions.loadWithdrawalDateSuccess, (state, value) => {
        if (isAchFormSA(state)) {
          let finalState = {
            ...state,
            standingAuthorizationDetails: {
              ...state.standingAuthorizationDetails,
              withdrawalDate: value.initiationDate
            }
          } as AchStandingAuthForm;
          if (state.extraPrompts.transferDirection === 'incoming') {
            //Will need settlementDate to send to cashiering(ProcessMoneyLinkSI -1 day)
            finalState.standingAuthorizationDetails.settlementDate = value.settlementDate;
            finalState.standingAuthorizationDetails.fundsAvailableToTrade = value.fundsCreditDate;
          }
          return finalState;
        }
        return { ...state };
    }),
    on(
      StandingAuthReviewPageActions.editClicked,
      (state, _) => {
        return {
          ...state,
          standingAuthorizationDetails: {
            ...state.standingAuthorizationDetails,
            receivingAccountHolderAddress: '',
        }          
        };
      }
    ),
);
